import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

interface ContactInfoFormSectionProps {
  form: UseFormReturn<Record<string, unknown>>;
  fields?: {
    email?: boolean;
    phone?: boolean;
    firstName?: boolean;
    lastName?: boolean;
  };
  required?: {
    email?: boolean;
    phone?: boolean;
    firstName?: boolean;
    lastName?: boolean;
  };
  disabled?: boolean;
  fieldPrefix?: string;
  fieldNames?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
  };
}

export function ContactInfoFormSection({
  form,
  fields = { email: true, phone: true, firstName: true, lastName: true },
  required = { email: true, phone: false, firstName: true, lastName: true },
  disabled = false,
  fieldPrefix = '',
  fieldNames = {},
}: ContactInfoFormSectionProps) {
  const { t } = useTranslation();

  const getFieldName = (baseFieldName: keyof typeof fieldNames) => {
    const customName = fieldNames[baseFieldName] || baseFieldName;
    return fieldPrefix ? `${fieldPrefix}.${customName}` : customName;
  };

  return (
    <div className="space-y-4">
      {fields.firstName && (
        <FormField
          control={form.control}
          name={getFieldName('firstName')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('usersActionDialog.labels.firstName')}
                {required.firstName && <span className="text-destructive ml-1">*</span>}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('usersActionDialog.placeholders.firstName')}
                  disabled={disabled}
                  {...field}
                  value={field.value as string ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {fields.lastName && (
        <FormField
          control={form.control}
          name={getFieldName('lastName')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('usersActionDialog.labels.lastName')}
                {required.lastName && <span className="text-destructive ml-1">*</span>}
              </FormLabel>
              <FormControl>
                <Input
                  placeholder={t('usersActionDialog.placeholders.lastName')}
                  disabled={disabled}
                  {...field}
                  value={field.value as string ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {fields.email && (
        <FormField
          control={form.control}
          name={getFieldName('email')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('usersActionDialog.labels.email')}
                {required.email && <span className="text-destructive ml-1">*</span>}
              </FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={t('usersActionDialog.placeholders.email')}
                  disabled={disabled}
                  {...field}
                  value={field.value as string ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {fields.phone && (
        <FormField
          control={form.control}
          name={getFieldName('phone')}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t('usersActionDialog.labels.phoneNumber')}
                {required.phone && <span className="text-destructive ml-1">*</span>}
              </FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  placeholder={t('usersActionDialog.placeholders.phoneNumber')}
                  disabled={disabled}
                  {...field}
                  value={field.value as string ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}